import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import { defaultValueFilter } from "@/components/pages/SinglePageApp/constants";
import { applicationReviewsKey } from "@/hooks/apis/queries/useApplicationReviewsInfiniteQuery/constants";
import type { IUseApplicationReviewsInfiniteQueryArguments } from "@/hooks/apis/queries/useApplicationReviewsInfiniteQuery/types";

import { pageAndLimitDefaultValue } from "@/common/constants/common";
import { applicationsApiHandler } from "@/services/apiHandlers/applications";

const useApplicationReviewsInfiniteQuery = ({
  filterBy,
  sortBy,
  applicationId,
}: IUseApplicationReviewsInfiniteQueryArguments) => {
  // this functionality should be changed when backend changed structure :))
  const byRateFilter =
    filterBy.value !== defaultValueFilter ? { rate: filterBy.value } : {};

  const {
    data: applicationReviews,
    isInitialLoading: isInitialLoadingReviews,
    fetchNextPage: fetchNextPageReviews,
    isFetchingNextPage: isFetchingNextPageReviews,
    hasNextPage: hasNextPageReviews = false,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [applicationReviewsKey, filterBy.value, sortBy.value],
    queryFn: ({ pageParam = 1 }) =>
      applicationsApiHandler.getApplicationReviews({
        page: pageParam,
        limit: pageAndLimitDefaultValue.limit,
        applicationId,
        orderBy: {
          column: sortBy.column,
          orientation: sortBy.orientation,
        },
        ...byRateFilter,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const {
        paginatorInfo: { hasMorePages = false, currentPage },
      } = lastPage;

      return hasMorePages ? currentPage + 1 : undefined;
    },
    enabled: Boolean(applicationId),
  });

  return {
    applicationReviews: useMemo(
      () =>
        applicationReviews?.pages.flatMap((application) => application.items) ||
        [],
      [applicationReviews?.pages],
    ),
    isInitialLoadingReviews,
    fetchNextPageReviews,
    isFetchingNextPageReviews,
    hasNextPageReviews,
  };
};

export default useApplicationReviewsInfiniteQuery;
